// Report row drill-down (legacy dblClickCB → OrderViewComponent / PaymentViewComponent /
// ExpenseViewComponent …). Loads the record (GET sales/{id}, purchases/{id}, …) and
// renders a compact summary: header facts, line items, totals and payments.

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import { adminApi, apiErrorMessage } from '../../api/client';
import { LoadingBlock } from '../../components/StateBlock';
import { formatDate, formatDateTime, t } from '../../i18n';
import { extrasSummary, isTruthyFlag, money, qty, relName, relNames, valueLabel } from './reportUtils';

export type DrilldownKind = 'sale' | 'purchase' | 'adjustment' | 'transfer' | 'payment' | 'expense' | 'income' | 'log' | 'apiLog';

const ENDPOINTS: Partial<Record<DrilldownKind, string>> = {
  sale: 'sales',
  purchase: 'purchases',
  adjustment: 'adjustments',
  transfer: 'transfers/stock',
  payment: 'payments',
  expense: 'expenses',
  income: 'incomes',
  log: 'logs'
};

type Rec = Record<string, unknown>;

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>
        {value ?? '-'}
      </Typography>
    </Stack>
  );
}

function Flag({ on, label, color }: { on: boolean; label: string; color: 'success' | 'warning' | 'error' | 'default' }) {
  return on ? <Chip size="small" color={color} label={label} /> : null;
}

function Json({ value }: { value: unknown }) {
  return (
    <Box component="pre" sx={{ m: 0, p: 1.5, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, maxHeight: 360 }}>
      {JSON.stringify(value ?? null, null, 2)}
    </Box>
  );
}

function OrderBody({ record, kind }: { record: Rec; kind: DrilldownKind }) {
  const items = Array.isArray(record.items) ? (record.items as Rec[]) : [];
  const payments = Array.isArray(record.payments) ? (record.payments as Rec[]) : [];
  const priceKey = kind === 'sale' ? 'price' : 'cost';
  const party =
    kind === 'sale' ? relName(record.customer) : kind === 'purchase' ? relName(record.supplier) : kind === 'transfer' ? null : relName(record.location);
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Flag on={isTruthyFlag(record.draft)} label={t('report.col.draft')} color="warning" />
        <Flag on={isTruthyFlag(record.paid)} label={t('report.col.paid')} color="success" />
        <Flag on={isTruthyFlag(record.void)} label={t('report.col.void')} color="error" />
        <Flag on={isTruthyFlag(record.pos)} label={t('report.col.pos')} color="default" />
        {record.type ? <Chip size="small" label={valueLabel(record.type)} /> : null}
        {record.status ? <Chip size="small" label={valueLabel(record.status)} /> : null}
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3 }}>
        <Fact label={t('report.col.reference')} value={String(record.reference ?? '-')} />
        <Fact label={t('report.col.date')} value={record.date ? formatDate(record.date) : formatDateTime(record.created_at)} />
        {kind === 'transfer' ? (
          <>
            <Fact label={t('report.col.fromLocation')} value={relName(record.from_location)} />
            <Fact label={t('report.col.toLocation')} value={relName(record.to_location)} />
          </>
        ) : (
          <Fact label={t(kind === 'sale' ? 'report.col.customer' : kind === 'purchase' ? 'report.col.supplier' : 'report.col.location')} value={party} />
        )}
        <Fact label={t('report.col.createdBy')} value={relName(record.user)} />
        <Fact label={t('report.col.createdAt')} value={formatDateTime(record.created_at)} />
      </Box>
      {items.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t('report.col.item')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  {t('report.col.quantity')}
                </TableCell>
                {kind !== 'transfer' ? (
                  <>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      {t(kind === 'sale' ? 'report.col.price' : 'report.col.cost')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      {t('report.col.subtotal')}
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, i) => (
                <TableRow key={String(item.id ?? i)}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {String(item.name ?? relName(item.item))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {String(item.code ?? (item.item as Rec | undefined)?.code ?? '')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{qty(item.quantity)}</TableCell>
                  {kind !== 'transfer' ? (
                    <>
                      <TableCell align="right">{money(item[priceKey] ?? item.unit_price ?? item.net_cost)}</TableCell>
                      <TableCell align="right">{money(item.subtotal)}</TableCell>
                    </>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      {kind !== 'transfer' ? (
        <Box sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 320 } }}>
          <Fact label={t('report.col.total')} value={money(record.total)} />
          <Fact label={t('report.col.itemTax')} value={money(record.item_tax_amount)} />
          <Fact label={t('report.col.orderTax')} value={money(record.order_tax_amount)} />
          <Fact label={t('report.col.discount')} value={money(record.discount_amount)} />
          <Fact label={t('report.col.shipping')} value={money(record.shipping ?? 0)} />
          <Fact label={t('report.col.grandTotal')} value={<strong>{money(record.grand_total)}</strong>} />
        </Box>
      ) : null}
      {payments.length ? (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('report.payments')}
          </Typography>
          <Table size="small">
            <TableBody>
              {payments.map((p, i) => (
                <TableRow key={String(p.id ?? i)}>
                  <TableCell>{String(p.reference ?? '-')}</TableCell>
                  <TableCell>{p.gateway ? valueLabel(p.gateway) : '-'}</TableCell>
                  <TableCell>{formatDateTime(p.created_at)}</TableCell>
                  <TableCell align="right">{money(p.amount)}</TableCell>
                  <TableCell align="right">{isTruthyFlag(p.received) ? t('report.col.received') : t('report.value.due')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      {record.details ? (
        <Alert severity="info" icon={false}>
          {String(record.details)}
        </Alert>
      ) : null}
    </Stack>
  );
}

function PaymentBody({ record }: { record: Rec }) {
  return (
    <Box>
      <Fact label={t('report.col.reference')} value={String(record.reference ?? '-')} />
      <Fact label={t('report.col.createdAt')} value={formatDateTime(record.created_at)} />
      <Fact label={t('report.col.amount')} value={<strong>{money(record.amount)}</strong>} />
      <Fact label={t('report.col.gateway')} value={record.gateway ? valueLabel(record.gateway) : '-'} />
      <Fact label={t('report.col.account')} value={relName(record.account)} />
      <Fact label={t('report.col.createdFor')} value={relName(record.payable)} />
      <Fact label={t('report.col.createdBy')} value={relName(record.user)} />
      <Fact label={t('report.col.received')} value={isTruthyFlag(record.received) ? t('status.yes') : t('status.no')} />
      <Fact label={t('report.col.customFields')} value={extrasSummary(record.extra_attributes)} />
      <Fact label={t('report.col.details')} value={String(record.details ?? '-')} />
    </Box>
  );
}

function AccountingBody({ record, kind }: { record: Rec; kind: DrilldownKind }) {
  return (
    <Box>
      <Fact label={t('report.col.title')} value={String(record.title ?? '-')} />
      <Fact label={t('report.col.reference')} value={String(record.reference ?? '-')} />
      <Fact label={t('report.col.date')} value={formatDate(record.date)} />
      <Fact label={t('report.col.amount')} value={<strong>{money(record.amount)}</strong>} />
      <Fact label={t('report.col.account')} value={relName(record.account)} />
      <Fact label={t('report.col.category')} value={relNames(record.categories)} />
      <Fact label={t('report.col.createdBy')} value={relName(record.user)} />
      {kind === 'expense' ? (
        <>
          <Fact label={t('report.col.approved')} value={isTruthyFlag(record.approved) ? t('status.yes') : t('status.no')} />
          <Fact label={t('report.col.approvedBy')} value={relName(record.approved_by ?? record.approvedBy)} />
        </>
      ) : null}
      <Fact label={t('report.col.customFields')} value={extrasSummary(record.extra_attributes)} />
      <Fact label={t('report.col.details')} value={String(record.details ?? '-')} />
    </Box>
  );
}

function LogBody({ record }: { record: Rec }) {
  return (
    <Stack spacing={2}>
      <Box>
        <Fact label={t('report.col.createdAt')} value={formatDateTime(record.created_at)} />
        <Fact label={t('report.col.logName')} value={String(record.log_name ?? '-')} />
        <Fact label={t('report.col.description')} value={String(record.description ?? '-')} />
        <Fact label={t('report.col.event')} value={String(record.event ?? '-')} />
        <Fact label={t('report.col.by')} value={relName(record.user ?? record.causer)} />
        <Fact label={t('report.col.subjectType')} value={String(record.subject_type ?? '-')} />
        <Fact label={t('report.col.subjectId')} value={String(record.subject_id ?? '-')} />
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {t('report.properties')}
      </Typography>
      <Json value={record.properties} />
      {record.subject ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('report.subject')}
          </Typography>
          <Json value={record.subject} />
        </>
      ) : null}
    </Stack>
  );
}

function ApiLogBody({ record }: { record: Rec }) {
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {String(record.endpoint ?? '')} · {String(record.method ?? '')} · {formatDateTime(record.created_at)}
      </Typography>
      <Json value={{ request: record.request_payload, response: record.response_payload }} />
    </Stack>
  );
}

export function RecordDetailDialog({ kind, row, onClose }: { kind: DrilldownKind | null; row: Rec | null; onClose: () => void }) {
  const [record, setRecord] = useState<Rec | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(kind && row);

  useEffect(() => {
    if (!kind || !row) return;
    const endpoint = ENDPOINTS[kind];
    setError(null);
    if (!endpoint || row.id == null) {
      setRecord(row);
      return;
    }
    let alive = true;
    setRecord(null);
    setLoading(true);
    adminApi
      .get(`${endpoint}/${row.id}`)
      .then((r) => alive && setRecord(r.data as Rec))
      .catch((e) => {
        if (!alive) return;
        setError(apiErrorMessage(e, t('report.unableToLoad')));
        setRecord(row);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [kind, row]);

  const title = kind ? t(`report.drilldown.${kind}`) : '';
  const reference = record?.reference ?? row?.reference;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={kind === 'payment' || kind === 'expense' || kind === 'income' ? 'sm' : 'md'}>
      <DialogTitle>
        {title}
        {reference ? ` (${String(reference)})` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {loading || !record || !kind ? (
          <LoadingBlock />
        ) : kind === 'payment' ? (
          <PaymentBody record={record} />
        ) : kind === 'expense' || kind === 'income' ? (
          <AccountingBody record={record} kind={kind} />
        ) : kind === 'log' ? (
          <LogBody record={record} />
        ) : kind === 'apiLog' ? (
          <ApiLogBody record={record} />
        ) : (
          <OrderBody record={record} kind={kind} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('action.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
